import { z } from "zod";
import { UserIdPublicSchema } from "./branded-id.schemas.js";
import { UserIdentitySchema } from "./user-identity.schemas.js";
import { UserCommunicationChannelSchema } from "./user-communication-channel.schemas.js";
import { UserRolesSchema } from "../constants/user-role.constants.js";
import { UserStatusSchema } from "./user-state.schemas.js";
import { UserAuthType, UserAuthTypeSchema } from "./user-auth-type.schemas.js";
import { SupportedTranslationLocaleSchema } from "./locale.schemas.js";

export const UserCreatableSchema = z
  .object({
    idPublic: UserIdPublicSchema,
    identity: UserIdentitySchema,
    communicationChannels: z.array(UserCommunicationChannelSchema).readonly(),
  })
  .readonly();

export type UserCreatable = z.infer<typeof UserCreatableSchema>;

export const UserSchema = UserCreatableSchema.unwrap()
  .extend({
    status: UserStatusSchema,
    authType: UserAuthTypeSchema.default(UserAuthType.PASSWORD),
    /**
     * Per-resource user roles. Single source of truth for user-aggregate
     * authorization. Defaults to `[]` for forward compatibility with
     * pre-migration documents.
     */
    roles: UserRolesSchema.default([]),
    /**
     * What language this human speaks, detected from what they write to Porter.
     * A user property rather than a participant one: a per-event field would
     * record whatever language that event forced, degrading the signal into
     * "language of the last event that messaged me". `null` until Porter has
     * detected something reliable, which is also how pre-migration documents
     * parse.
     */
    preferredLocale: SupportedTranslationLocaleSchema.nullable().default(null),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .readonly();

export type User = z.infer<typeof UserSchema>;
